import { Component, defineElement } from '@theme/component';

class YiseVariantComparison extends Component {
  ready() {
    if (this.initialized) return;
    const signal = this.abortController.signal;
    const initialize = () => {
      if (signal.aborted || !this.isConnected || this.abortController?.signal !== signal) return false;
      if (this.initialized) return true;
      const container = this.querySelector('.vc-popup');
      const overlay = this.querySelector('.js-vc-popup-overlay');
      const swiperEl = this.querySelector('.js-vc-swiper');
      const prevBtn = this.querySelector('.js-vc-swiper-prev');
      const nextBtn = this.querySelector('.js-vc-swiper-next');
      const trigger = this.querySelector('.js-vc-popup-trigger');
      const closeBtn = this.querySelector('.js-vc-popup-close');
      const data = this.querySelector('.js-vc-popup-data');
      if (!container || !overlay || !swiperEl || !prevBtn || !nextBtn || !trigger || !closeBtn || !data) return false;
      let config;
      try { config = JSON.parse(data.textContent); } catch { return false; }
      if (!config || typeof config !== 'object' || !Array.isArray(config.variants)) return false;

      this.parserObserver?.disconnect();
      this.parserObserver = null;
      Object.assign(this, { container, overlay, swiperEl, prevBtn, nextBtn, trigger, closeBtn, config });
      this.initialized = true;
      this.connectControls(signal);
      return true;
    };
    if (initialize()) return;
    this.parserObserver?.disconnect();
    this.parserObserver = new MutationObserver(initialize);
    this.parserObserver.observe(this, { childList: true, characterData: true, subtree: true });
  }

  connectControls(signal) {
    this.listen(this.trigger, 'click', (event) => {
      event.preventDefault();
      this.openPopup();
    });
    this.listen(this.closeBtn, 'click', (event) => {
      event.preventDefault();
      this.closePopup();
    });
    this.listen(this.overlay, 'click', (event) => {
      if (event.target === this.overlay) this.closePopup();
    });
    this.listen(this.overlay, 'close', () => this.releaseScrollLock());
    this.listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && this.overlay.open) this.closePopup();
    });

    const initializeWhenOpen = () => {
      if (!signal.aborted && this.abortController?.signal === signal && this.isConnected && !this.swiperInstance) this.ensureSwiper();
    };
    this.listen(window, 'yise:swiper-ready', initializeWhenOpen);
    this.listen(window, 'yise:theme-scripts:ready', initializeWhenOpen);
    window.yiseThemeScriptsReady?.then(initializeWhenOpen, () => {});
  }

  teardown() {
    this.parserObserver?.disconnect();
    this.parserObserver = null;
    this.initialized = false;
    this.closePopup();
    this.releaseScrollLock();
    this.swiperInstance?.destroy(true, true);
    this.swiperInstance = null;
    this.container?.classList.remove('swiper-initialized');
  }

  getCurrentVariantId() {
    const productForm = this.closest('yise-product-form[data-template-product]');
    return productForm?.currentVariant?.id || new URLSearchParams(window.location.search).get('variant');
  }

  updateSelectedIndicator() {
    const currentVariantId = this.getCurrentVariantId();
    this.container.querySelectorAll('.vc-popup_selected-indicator').forEach((indicator) => {
      const isActive = indicator.getAttribute('data-variant-id') == currentVariantId;
      indicator.classList.toggle('is-active', isActive);
      if (isActive) indicator.setAttribute('aria-current', 'true');
      else indicator.removeAttribute('aria-current');
    });
  }

  openPopup() {
    if (this.overlay.open) return;
    this.previousFocus = document.activeElement;
    this.previousHtmlOverflow = document.documentElement.style.overflow;
    this.previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this.scrollLocked = true;
    document.documentElement.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    this.overlay.showModal();
    this.ensureSwiper();
    this.updateSelectedIndicator();
  }

  closePopup() {
    if (this.overlay?.open) this.overlay.close();
  }

  releaseScrollLock() {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;
    document.documentElement.style.overflow = this.previousHtmlOverflow;
    document.body.style.paddingRight = this.previousBodyPaddingRight;
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    this.previousFocus = null;
  }

  findSlideIndexByVariantId(variantId) {
    const slides = this.swiperEl.querySelectorAll('.swiper-slide');
    for (let index = 0; index < slides.length; index++) {
      if (slides[index].getAttribute('data-variant-id') == variantId) return index;
    }
    return 0;
  }

  ensureSwiper() {
    if (!this.overlay.open) return;
    const currentVariantId = this.getCurrentVariantId();
    const slideIndex = currentVariantId ? this.findSlideIndexByVariantId(currentVariantId) : 0;
    if (this.swiperInstance) {
      this.swiperInstance.slideTo(slideIndex, 0);
      this.swiperInstance.update();
    } else if (typeof window.Swiper === 'function') {
      this.initSwiper(slideIndex);
    }
  }

  initSwiper(initialSlide) {
    const signal = this.abortController.signal;
    const isCurrent = () => !signal.aborted && this.isConnected && this.abortController?.signal === signal;
    const syncComparisonAccessibility = (swiper) => {
      if (!isCurrent()) return;
      const bounds = swiper.el.getBoundingClientRect();
      swiper.slides.forEach((slide) => {
        const slideBounds = slide.getBoundingClientRect();
        const isVisible = slideBounds.left >= bounds.left - 1 && slideBounds.right <= bounds.right + 1;
        slide.toggleAttribute('inert', !isVisible);
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
      this.prevBtn.setAttribute('aria-disabled', swiper.isBeginning ? 'true' : 'false');
      this.nextBtn.setAttribute('aria-disabled', swiper.isEnd ? 'true' : 'false');
    };

    this.swiperInstance = new window.Swiper(this.swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      grabCursor: true,
      centerInsufficientSlides: true,
      centeredSlides: true,
      initialSlide: initialSlide || 0,
      touchAngle: 75,
      touchMoveStopPropagation: true,
      a11y: {
        containerMessage: this.swiperEl.getAttribute('aria-label'),
        containerRoleDescriptionMessage: 'carousel',
        slideLabelMessage: 'Comparison option \u007b\u007bindex\u007d\u007d of \u007b\u007bslidesLength\u007d\u007d',
        prevSlideMessage: 'Previous comparison option',
        nextSlideMessage: 'Next comparison option'
      },
      mousewheel: {
        forceToAxis: true
      },
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
        disabledClass: 'is-disabled'
      },
      breakpoints: {
        480: {
          centeredSlides: false
        }
      },
      on: {
        init: (swiper) => {
          if (!isCurrent()) return;
          this.container.classList.add('swiper-initialized');
          syncComparisonAccessibility(swiper);
        },
        slideChange: syncComparisonAccessibility,
        transitionEnd: syncComparisonAccessibility,
        resize: syncComparisonAccessibility,
        update: syncComparisonAccessibility
      }
    });
  }
}

defineElement('yise-variant-comparison', YiseVariantComparison);
